/**
 * Development-time report of catalog problems: keys that no catalog of the chain holds, keys that were
 * answered by the fallback language, and messages whose values did not arrive, so their reduced form was
 * shown instead. It never throws, so a missing text cannot break a screen.
 */
export type Kind = 'absent' | 'fallback' | 'incomplete';

export class Missing {
	#entries = new Map<string, { key: string; locale: string; kind: Kind }>();
	#loud: boolean;

	/**
	 * @param loud Print each new problem once to the console; on in development
	 */
	constructor(loud = false) {
		this.#loud = loud;
	}

	get entries() {
		return [...this.#entries.values()];
	}

	/**
	 * Keys that no language holds: these are defects, a fallback is only a pending translation
	 */
	get absent() {
		return this.entries.filter(entry => entry.kind === 'absent').map(entry => entry.key);
	}

	/**
	 * Messages rendered without the values they name: the event that carried them did not satisfy its contract
	 */
	get incomplete() {
		return this.entries.filter(entry => entry.kind === 'incomplete').map(entry => entry.key);
	}

	add(key: string, locale: string, kind: Kind) {
		const id = `${kind}:${locale}:${key}`;
		if (this.#entries.has(id)) return;
		this.#entries.set(id, { key, locale, kind });
		this.#loud && console.warn(`[i18n] ${Missing.#reports[kind](locale)} "${key}"`);
	}

	static #reports: Record<Kind, (locale: string) => string> = {
		absent: () => 'No catalog holds',
		fallback: locale => `"${locale}" falls back to English for`,
		incomplete: () => 'Values were missing, so the reduced form was shown for'
	};

	clear() {
		this.#entries.clear();
	}
}
